/**
 * Merge-tag rendering for operator-authored message templates.
 *
 * Balance reminders let each operator write their own SMS and email copy, with
 * `{{placeholders}}` for the trip details. That copy goes straight to their
 * customers, so the two failure modes that matter are:
 *
 *   1. A tag the operator invented, or mistyped, reaching a customer as literal
 *      `{{custmer_name}}`. Unknown tags render EMPTY instead — a slightly terse
 *      message beats one that looks broken.
 *   2. An apostrophe in a customer's name breaking the HTML of an email. Values
 *      are escaped when rendering HTML, never when rendering SMS (where escaping
 *      would show `&amp;` to the reader).
 *
 * Pure and immutable; the same renderer drives the Builder's live preview and
 * the edge function that actually sends, so what an operator previews is what
 * their customer receives.
 */

/** The values a balance-reminder template can interpolate. */
export type TemplateValues = Record<string, string | number | null | undefined>

/** Matches `{{ tag_name }}` with any surrounding whitespace. */
const TAG_PATTERN = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g

const HTML_ESCAPES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
}

/** Escape a value for interpolation into HTML. */
export function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (char) => HTML_ESCAPES[char] ?? char)
}

export interface RenderOptions {
  /**
   * Escape interpolated values for HTML. Use for email bodies; leave off for
   * SMS and email SUBJECTS, which are plain text.
   */
  html?: boolean
}

/**
 * Render a template, substituting `{{tags}}` from `values`.
 *
 * A tag with no matching value — or a null/undefined one — renders as an empty
 * string. That is deliberate: an operator who writes `{{flight_number}}` into
 * every reminder should get a slightly emptier sentence on the trips that have
 * no flight, not the literal braces.
 */
export function renderTemplate(
  template: string,
  values: TemplateValues,
  options: RenderOptions = {}
): string {
  if (!template) return ''

  return template.replace(TAG_PATTERN, (_match, tag: string) => {
    const value = values[tag]
    if (value === null || value === undefined) return ''
    const asString = String(value)
    return options.html ? escapeHtml(asString) : asString
  })
}

/**
 * The tags a template may use, for the Builder's reference list.
 *
 * Exported as data rather than documented in prose so the UI and the sender
 * cannot drift: if a tag is listed here it is supplied by the sender.
 */
export const BALANCE_REMINDER_TAGS = [
  { tag: 'customer_name', description: "The customer's name" },
  { tag: 'first_name', description: "The customer's first name" },
  { tag: 'company_name', description: 'Your company name' },
  { tag: 'confirmation_code', description: 'The booking reference' },
  { tag: 'pickup_datetime', description: 'Pickup date and time' },
  { tag: 'pickup_date', description: 'Pickup date only' },
  { tag: 'pickup_time', description: 'Pickup time only' },
  { tag: 'pickup_address', description: 'Pickup address' },
  { tag: 'dropoff_address', description: 'Dropoff address' },
  { tag: 'vehicle_name', description: 'The booked vehicle' },
  { tag: 'total_amount', description: 'Trip total' },
  { tag: 'amount_paid', description: 'Paid so far' },
  { tag: 'balance_due', description: 'Amount still owed' },
  { tag: 'payment_link', description: 'Link for the customer to pay' },
] as const

/**
 * Find tags in a template that the sender will not supply.
 *
 * Used by the Builder to warn at edit time — the render itself blanks them
 * silently, which is right at send time but unhelpful while writing.
 */
export function findUnknownTags(template: string): string[] {
  const known = new Set<string>(BALANCE_REMINDER_TAGS.map((t) => t.tag))
  const unknown = new Set<string>()

  for (const match of template.matchAll(TAG_PATTERN)) {
    const tag = match[1]
    if (!known.has(tag)) unknown.add(tag)
  }

  return [...unknown]
}

/**
 * Default copy, so an operator can switch reminders on without writing anything.
 *
 * Kept deliberately plain and short: an SMS is charged per segment, and the
 * useful content is the amount and the link.
 */
export const DEFAULT_BALANCE_REMINDER_TEMPLATES = {
  customerSms:
    'Hi {{first_name}}, a balance of {{balance_due}} is due for your {{pickup_date}} ride with {{company_name}}. Pay here: {{payment_link}}',
  customerEmailSubject: 'Balance due for your {{pickup_date}} ride',
  customerEmailBody: [
    '<p>Hi {{first_name}},</p>',
    '<p>Your ride with {{company_name}} is coming up on {{pickup_datetime}}, and a balance of <strong>{{balance_due}}</strong> is still outstanding.</p>',
    '<p><strong>Booking:</strong> {{confirmation_code}}<br/>',
    '<strong>Pickup:</strong> {{pickup_address}}<br/>',
    '<strong>Trip total:</strong> {{total_amount}}<br/>',
    '<strong>Paid so far:</strong> {{amount_paid}}</p>',
    '<p><a href="{{payment_link}}">Pay your balance</a></p>',
    '<p>Thank you,<br/>{{company_name}}</p>',
  ].join('\n'),
  operatorSms:
    '{{customer_name}} has {{balance_due}} outstanding for {{pickup_date}} ({{confirmation_code}}).',
  operatorEmailSubject: 'Balance outstanding: {{customer_name}} on {{pickup_date}}',
  operatorEmailBody: [
    '<p>A booking with an outstanding balance is coming up.</p>',
    '<p><strong>Customer:</strong> {{customer_name}}<br/>',
    '<strong>Booking:</strong> {{confirmation_code}}<br/>',
    '<strong>Pickup:</strong> {{pickup_datetime}} — {{pickup_address}}<br/>',
    '<strong>Vehicle:</strong> {{vehicle_name}}<br/>',
    '<strong>Trip total:</strong> {{total_amount}}<br/>',
    '<strong>Paid so far:</strong> {{amount_paid}}<br/>',
    '<strong>Balance due:</strong> {{balance_due}}</p>',
    '<p>Payment link sent to the customer: {{payment_link}}</p>',
  ].join('\n'),
} as const
